import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/hooks/use-auth';

export interface PipelinePreferencesState {
  collapsedStageIds: number[];
  layout: 'standard' | 'compact';
  viewMode: 'pipeline' | 'calendar';
  scrollPositions: Record<number, number>;
  focusMatchesOnly: boolean;
  filters: Record<string, any>;
  searchText: string;
}

export const defaultPipelinePreferences: PipelinePreferencesState = {
  collapsedStageIds: [],
  layout: 'standard',
  viewMode: 'pipeline',
  scrollPositions: {},
  focusMatchesOnly: true,
  filters: {},
  searchText: '',
};

interface UsePipelinePreferencesResult<T extends PipelinePreferencesState> {
  preferences: T;
  updatePreferences: (
    updates: Partial<T> | ((previous: T) => T)
  ) => void;
  resetPreferences: () => void;
}

/**
 * Persist pipeline layout preferences per-user using localStorage.
 * Falls back gracefully when unavailable (SSR / private browsing).
 */
export function usePipelinePreferences<T extends PipelinePreferencesState>(
  defaults: T
): UsePipelinePreferencesResult<T> {
  const { user } = useAuth();
  const storageKey = useMemo(
    () => `pipeline-preferences:${user?.id ?? 'guest'}`,
    [user?.id]
  );
  const [preferences, setPreferences] = useState<T>(defaults);
  const hasHydratedFromStorage = useRef(false);

  // Hydrate from localStorage whenever the storage key changes (user switch)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) {
        setPreferences(defaults);
        hasHydratedFromStorage.current = true;
        return;
      }

      const parsed = JSON.parse(raw);
      setPreferences({ ...defaults, ...parsed });
      hasHydratedFromStorage.current = true;
    } catch (error) {
      console.warn('[PipelinePreferences] Failed to read from storage:', error);
      setPreferences(defaults);
      hasHydratedFromStorage.current = true;
    }
  }, [storageKey, defaults]);

  // Persist changes to localStorage once hydration has completed
  useEffect(() => {
    if (!hasHydratedFromStorage.current) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.warn('[PipelinePreferences] Failed to write to storage:', error);
    }
  }, [preferences, storageKey]);

  const updatePreferences = useCallback(
    (updates: Partial<T> | ((previous: T) => T)) => {
      setPreferences((previous) => {
        const nextState =
          typeof updates === 'function'
            ? (updates(previous) as T)
            : ({ ...previous, ...updates } as T);
        return nextState;
      });
    },
    []
  );

  const resetPreferences = useCallback(() => {
    setPreferences(defaults);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(storageKey);
      } catch (error) {
        console.warn('[PipelinePreferences] Failed to reset storage:', error);
      }
    }
  }, [defaults, storageKey]);

  return {
    preferences,
    updatePreferences,
    resetPreferences,
  };
}
